/**
 * KeywordInputForm
 *
 * Handles the keyword textarea + blog title input used in the "I have keywords"
 * (have-keywords) entry flow of ContentPlannerPage.
 *
 * Responsibilities:
 * - Multi-line keyword textarea (one keyword per line)
 * - Inline keyword-count badge
 * - KeywordDedupWarning integration
 * - Researched keywords panel (completed topical maps)
 * - AI topic suggestions panel
 * - Blog title input with SERP-powered "Suggest" button
 * - Title suggestions dropdown
 */

import React, { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  ListPlus,
  AlertCircle,
  Database,
  Layers,
  ChevronDown,
  ChevronRight,
  PenLine,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { KeywordDedupWarning } from '@/components/content-planner/KeywordDedupWarning';
import { ResearchedKeywordsPanel } from '@/components/content-planner/ResearchedKeywordsPanel';
import { TopicSuggestionsPanel } from '@/components/content-planner/TopicSuggestionsPanel';
import { ClusterKeywordDropdown } from '@/components/content-planner/ClusterKeywordDropdown';
import type { BrandBible } from '@/types/tenant';

// ── Minimal shape of a completed topical map needed by this form ────────────
export interface CompletedMap {
  map_id: string;
  seed_keyword: string;
  total_clusters: number;
  total_keywords: number;
  total_volume?: number;
}

export interface KeywordInputFormProps {
  /** Selected template's contentType, forwarded to topic suggestions. */
  templateContentType?: string | null;
  // ── Keyword textarea ──────────────────────────────────────────────────────
  keywordsText: string;
  onKeywordsChange: (value: string) => void;
  keywordCount: number;
  keywordsError?: string;
  /** Quick Create v2 (#row3): Topic + Title become required. */
  requireTopicTitle?: boolean;
  topicError?: string;
  titleError?: string;

  // ── Tenant / data ─────────────────────────────────────────────────────────
  tenantId: string | null;
  brandBible: BrandBible | null;

  // ── Researched keywords panel ─────────────────────────────────────────────
  completedMaps: CompletedMap[];
  mapsLoading: boolean;
  mapsPending: boolean;
  expandedMapId: string | null;
  onToggleMap: (mapId: string) => void;
  onShowMoreResearch: () => void;
  totalMapsCount: number;

  // ── Keyword helpers ───────────────────────────────────────────────────────
  addKeywordToTextarea: (keyword: string) => void;
  addKeywordsToTextarea: (keywords: string[]) => void;
  currentKeywordsSet: Set<string>;
  parseKeywords: (text: string) => string[];

  // ── Blog title ────────────────────────────────────────────────────────────
  blogTitle: string;
  onBlogTitleChange: (value: string) => void;
  selectedTopic: string;
  onTopicSelect: (topic: string) => void;

  // ── Title suggestions ─────────────────────────────────────────────────────
  isSuggestingTitles: boolean;
  titleSuggestions: string[];
  competitorCount: number;
  titleSuggestMessage: string;
  onSuggestTitles: () => void;
  onSelectTitleSuggestion: (suggestion: string) => void;

  // ── Misc ──────────────────────────────────────────────────────────────────
  isSubmitting: boolean;
}

export const KeywordInputForm: React.FC<KeywordInputFormProps> = ({
  templateContentType,
  keywordsText,
  onKeywordsChange,
  keywordCount,
  keywordsError,
  requireTopicTitle = false,
  topicError,
  titleError,
  tenantId,
  brandBible,
  completedMaps,
  mapsLoading,
  mapsPending,
  expandedMapId,
  onToggleMap,
  onShowMoreResearch,
  totalMapsCount,
  addKeywordToTextarea,
  addKeywordsToTextarea,
  currentKeywordsSet,
  parseKeywords,
  blogTitle,
  onBlogTitleChange,
  selectedTopic,
  onTopicSelect,
  isSuggestingTitles,
  titleSuggestions,
  competitorCount,
  titleSuggestMessage,
  onSuggestTitles,
  onSelectTitleSuggestion,
  isSubmitting,
}) => {
  // Examples come from what this tenant actually sells, then from keywords they
  // have already researched. The old hardcoded water-heater examples showed up
  // on every tenant — confusing on, say, a locksmith workspace (#273).
  const keywordsPlaceholder = useMemo(() => {
    const services = (brandBible?.services_offered ?? [])
      .map((s) => s?.trim())
      .filter(Boolean) as string[];
    const researched = completedMaps
      .map((m) => m.seed_keyword?.trim())
      .filter(Boolean) as string[];

    const examples = (services.length ? services : researched).slice(0, 3);

    const lines = examples.length
      ? examples
      : // Nothing known about the tenant yet — stay industry-neutral rather than
        // inventing a business they aren't in.
        ['your main service', 'your service + your city', 'how to choose a provider'];

    return `Enter keywords (one per line):\n\n${lines.join('\n')}`;
  }, [brandBible, completedMaps]);

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 rounded-full bg-teal text-white flex items-center justify-center text-sm font-medium">
          1
        </div>
        <h3 className="font-semibold">Enter Keywords</h3>
      </div>

      {/* ── Clusters-first dropdown (Quick Create v2, #1306) ─────────────────
          Chans: "the very first thing should be the dropdown of our clusters."
          Self-hides when the tenant has no service clusters. */}
      <ClusterKeywordDropdown
        tenantId={tenantId}
        onAddKeyword={addKeywordToTextarea}
        currentKeywordsSet={currentKeywordsSet}
        disabled={isSubmitting}
      />

      {/* ── Keyword textarea ─────────────────────────────────────────────── */}
      <div className="space-y-2">
        <Label htmlFor="keywords" className="flex items-center gap-2">
          <ListPlus className="h-4 w-4" />
          Target Keywords
          {keywordCount > 0 && (
            <Badge variant="secondary">
              {keywordCount} keyword{keywordCount !== 1 ? 's' : ''}
            </Badge>
          )}
        </Label>
        <Textarea
          id="keywords"
          placeholder={keywordsPlaceholder}
          value={keywordsText}
          onChange={(e) => onKeywordsChange(e.target.value)}
          className={cn(
            'min-h-[140px] font-mono text-sm',
            keywordsError && 'border-destructive'
          )}
        />
        {keywordsError && (
          <p className="text-sm text-destructive flex items-center gap-1">
            <AlertCircle className="h-4 w-4" />
            {keywordsError}
          </p>
        )}

        {/* Keyword dedup warning */}
        <KeywordDedupWarning
          primaryKeyword={parseKeywords(keywordsText)[0] || ''}
          tenantId={tenantId}
        />
      </div>

      {/* ── Researched keywords (completed topical maps) ─────────────────── */}
      {(mapsLoading || mapsPending || completedMaps.length > 0 || totalMapsCount === 0) && (
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-teal" />
              <span className="text-sm font-medium text-muted-foreground">
                Or select from your researched keywords
              </span>
            </div>
            {(mapsLoading || mapsPending) ? (
              <Skeleton className="h-5 w-28" />
            ) : (
              <Badge variant="secondary" className="text-xs">
                {completedMaps.length} research session{completedMaps.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>

          <div className="space-y-2">
            {(mapsLoading || mapsPending) ? (
              <>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="border rounded-lg overflow-hidden">
                    <div className="p-3 bg-muted flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-4 w-4" />
                        <Skeleton className="h-4 w-48" />
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-3 w-16" />
                          <Skeleton className="h-3 w-20" />
                          <Skeleton className="h-3 w-16" />
                        </div>
                      </div>
                      <Skeleton className="h-4 w-4" />
                    </div>
                  </div>
                ))}
              </>
            ) : completedMaps.length === 0 ? (
              <button
                onClick={onShowMoreResearch}
                className="w-full flex items-center justify-center gap-2 py-4 border border-dashed rounded-lg text-sm text-muted-foreground hover:border-teal hover:text-teal transition-colors"
              >
                <Database className="h-4 w-4" />
                No research sessions yet — start one to discover keywords
              </button>
            ) : (
              <>
                {completedMaps.slice(0, 3).map((map) => (
                  <div key={map.map_id} className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => onToggleMap(map.map_id)}
                      className="w-full flex items-center justify-between p-3 bg-muted hover:bg-muted transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Layers className="h-4 w-4 text-teal shrink-0" />
                          <span className="font-medium text-sm truncate">{map.seed_keyword}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                          <span>{map.total_clusters} clusters</span>
                          <span className="text-slate-300">|</span>
                          <span>{map.total_keywords} keywords</span>
                          <span className="text-slate-300">|</span>
                          <span>{map.total_volume?.toLocaleString() || 0} vol</span>
                        </div>
                      </div>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 text-muted-foreground transition-transform shrink-0',
                          expandedMapId === map.map_id && 'rotate-180'
                        )}
                      />
                    </button>

                    {expandedMapId === map.map_id && (
                      <div className="p-3 bg-card border-t">
                        <ResearchedKeywordsPanel
                          mapId={map.map_id}
                          tenantId={tenantId}
                          onAddKeyword={addKeywordToTextarea}
                          onAddKeywords={addKeywordsToTextarea}
                          currentKeywordsSet={currentKeywordsSet}
                        />
                      </div>
                    )}
                  </div>
                ))}

                {totalMapsCount > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 h-auto text-xs"
                    onClick={onShowMoreResearch}
                  >
                    View all {totalMapsCount} research {totalMapsCount === 1 ? 'session' : 'sessions'}
                    <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── AI Topic Suggestions ─────────────────────────────────────────── */}
      {keywordCount > 0 && (
        <div className="mt-4 pt-4 border-t">
          <TopicSuggestionsPanel
            templateContentType={templateContentType}
            seedKeywords={parseKeywords(keywordsText)}
            brandBible={brandBible}
            onAddKeyword={addKeywordToTextarea}
            currentKeywordsSet={currentKeywordsSet}
            onTopicSelect={onTopicSelect}
          />
        </div>
      )}

      {/* ── Blog Title input ─────────────────────────────────────────────── */}
      {keywordCount > 0 && (
        <div className="mt-6 pt-6 border-t">
          <div className="space-y-2">
            <Label htmlFor="blogTitle" className="text-sm font-medium flex items-center gap-2">
              <PenLine className="h-4 w-4" />
              Blog Title
              {selectedTopic && (
                <Badge variant="secondary" className="text-xs font-normal">
                  Topic: {selectedTopic.length > 35 ? selectedTopic.slice(0, 35) + '...' : selectedTopic}
                </Badge>
              )}
              {requireTopicTitle ? (
                <span className="text-destructive font-normal">*</span>
              ) : (
                <span className="text-muted-foreground font-normal">(optional)</span>
              )}
            </Label>
            <div className="flex gap-2">
              <Input
                id="blogTitle"
                value={blogTitle}
                onChange={(e) => onBlogTitleChange(e.target.value)}
                placeholder={requireTopicTitle ? 'Enter a title for this article' : 'Leave blank for AI-generated title'}
                maxLength={100}
                disabled={isSubmitting}
                className={cn('flex-1', titleError && 'border-destructive')}
              />
              <Button
                variant="outline"
                onClick={onSuggestTitles}
                disabled={isSuggestingTitles || keywordCount === 0}
                className={`${isSuggestingTitles || keywordCount === 0 ? "" : "animate-ai-suggest"} text-teal border-teal/30 hover:bg-teal-light hover:border-teal/50`}
              >
                {isSuggestingTitles ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin mr-1.5" />
                    Researching...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-1.5" />
                    Suggest
                  </>
                )}
              </Button>
            </div>

            {titleError && (
              <p className="mt-1 text-xs text-destructive">{titleError}</p>
            )}
            {requireTopicTitle && topicError && !selectedTopic && (
              <p className="mt-1 text-xs text-destructive">{topicError}</p>
            )}

            {/* Progress message during title suggest */}
            {isSuggestingTitles && (
              <div className="mt-2 flex items-center gap-2 text-xs text-teal-dark">
                <RefreshCw className="h-3 w-3 animate-spin shrink-0" />
                <span className="animate-pulse">{titleSuggestMessage}</span>
              </div>
            )}

            {/* Title suggestions list */}
            {titleSuggestions.length > 0 && (
              <div className="mt-2 p-3 bg-teal-light border border-teal/30 rounded-lg">
                <p className="text-xs text-teal-dark mb-2 flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  {competitorCount > 0
                    ? `Based on ${competitorCount} competitor titles:`
                    : selectedTopic
                    ? `AI-researched titles for "${selectedTopic.length > 30 ? selectedTopic.slice(0, 30) + '...' : selectedTopic}":`
                    : 'Suggested titles based on SEO patterns:'}
                </p>
                <div className="space-y-1">
                  {titleSuggestions.map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => onSelectTitleSuggestion(suggestion)}
                      className={cn(
                        'w-full text-left px-2 py-1.5 text-sm rounded transition-colors',
                        'hover:bg-teal-light/70',
                        blogTitle === suggestion && 'bg-teal-light/70 font-medium'
                      )}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              If left blank, AI will generate an SEO-optimized title based on your keyword.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
};
